import Taro, { Current } from '@tarojs/taro';
import wx from 'weixin-js-sdk';
import HomeApi from '@/api/home';
import shootInfoApi from '@/api/shootInfo';
import { conformsTo } from 'lodash';

const MobileDetect = require('mobile-detect');
const TAB_BAR_HEIGHT = 50;
const NAVIGATOR_HEIGHT = 44;
const SIGN_REGEXP = /([YMDhsm])(\1*)/g;
const DEFAULT_PATTERN = 'YYYY-MM-DD';

/**
 * 获取异步缓存
 * @param key
 */
export function getStorage(key: string) {
  return Taro.getStorage({ key })
    .then((res) => res.data)
    .catch(() => '');
}
/**
 * 获取同步缓存
 * @param key
 */
export function getStorageSync(key: string) {
  return Taro.getStorageSync(key);
}

/**
 * 存储缓存
 * @param key
 * @param data
 */
export function setStorage(key: string, data: any) {
  return Taro.setStorage({
    key,
    data
  });
}

/**
 * 异步获取历史路由堆栈并设置登陆的回调路由
 */
export function setLoginReturnUrl() {
  // 需要延时获取
  const pageArray = Taro.getCurrentPages();
  const currentPage = pageArray[pageArray.length - 1];
  setStorage('return_url', currentPage?.path || '/pages/home');
}

/**
 * 全局校验用户是否登陆
 */
export async function globalLoginCheck(callback = () => {}) {
  const authToken = getStorageSync('id_token');
  const loginRedirect = () => {
    setTimeout(() => {
      setLoginReturnUrl();
    }, 500);
    if (window.sessionStorage.getItem('login_entry')) {
      Taro.redirectTo({
        url: `/pages/home`
      });
      window.sessionStorage.removeItem('login_entry');
    } else {
      Taro.navigateTo({
        url: `/pages/login`
      });
    }
  };
  if (authToken) {
    if (callback && typeof callback === 'function') {
      callback();
    }
  } else {
    loginRedirect();
  }
}
/**
 * 全局退出登陆
 */
export function globalLoginOut(callback = () => {}) {
  if (getStorageSync('id_token')) {
    Taro.removeStorage({
      key: 'id_token',
      success: function(res) {
        if (callback && typeof callback === 'function') {
          callback();
        } else {
          console.log(res);
        }
      }
    });
    if (getStorageSync('return_url')) {
      try {
        Taro.removeStorageSync('return_url');
      } catch (e) {
        console.log(e);
      }
    }
  } else {
    if (callback && typeof callback === 'function') {
      callback();
    }
  }
}

/**
 * 获取窗口高度，适配多端
 * @param showTabBar
 */
export function getWindowHeight(showTabBar = true) {
  const info = Taro.getSystemInfoSync();

  const { windowHeight, statusBarHeight, titleBarHeight } = info;
  const tabBarHeight = showTabBar ? TAB_BAR_HEIGHT : 0;

  if (process.env.TARO_ENV === 'rn') {
    return windowHeight - statusBarHeight - NAVIGATOR_HEIGHT - tabBarHeight;
  }

  if (process.env.TARO_ENV === 'h5') {
    return `${windowHeight - tabBarHeight}px`;
  }

  if (process.env.TARO_ENV === 'alipay') {
    return `${windowHeight -
      statusBarHeight -
      titleBarHeight +
      (showTabBar ? 0 : TAB_BAR_HEIGHT)}px`;
  }

  return `${windowHeight}px`;
}

/**
 *
 * @param price 价格转换
 */
export const formatPrice = (price: number, keepTwo = false) => {
  if (!Number.isNaN(price)) {
    const yuan = price / 100;
    if (Number.isInteger(yuan)) {
      return yuan;
    } else {
      return keepTwo ? +yuan.toFixed(2) : yuan;
    }
  }
  return '';
};

/**
 * 计算当前时间
 */
export const calStateTime = () => {
  const now = new Date();
  const year: number = now.getFullYear();
  let month: number | string = now.getMonth() + 1;
  let day: number | string = now.getDay();
  let hour: number | string = now.getHours();
  let minute: number | string = now.getMinutes();
  let second: number | string = now.getSeconds();
  if (month < 10) {
    month = '0' + month;
  }
  if (day < 10) {
    day = '0' + day;
  }
  if (hour < 10) {
    hour = '0' + hour;
  }
  if (minute < 10) {
    minute = '0' + minute;
  }
  if (second < 10) {
    second = '0' + second;
  }
  const startDate = `${year}/${month}/${day}`;
  const startTime = `${hour}:${minute}`;
  return { startDate, startTime };
};
/**
 * 环境检查
 */
export const isWX = () => {
  const ua = window.navigator.userAgent.toLowerCase();
  if (ua.match(/MicroMessenger/i) == 'micromessenger') {
    return true; // 是微信端
  } else {
    return false;
  }
};
export const checkAgent = () => {
  const ua = window.navigator.userAgent;
  const obj = { isWechat: false, isAndroid: false, isIos: false, isAlipay: false };
  if (ua.indexOf('MicroMessenger') > -1) {
    obj.isWechat = true;
  }
  if (ua.indexOf('AlipayClient') > -1) {
    obj.isAlipay = true;
  }
  if (ua.indexOf('Android') > -1 || ua.indexOf('Linux') > -1) {
    obj.isAndroid = true;
  }
  if (ua.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/)) {
    obj.isIos = true;
  }
  return obj;
};
/**
 * 检查app端
 */
export const checkAppClient = () => {
  const obj = { isAndroid: false, isIos: false };
  if (checkAgent().isIos) {
    if (
      window.webkit &&
      window.webkit.messageHandlers &&
      window.webkit.messageHandlers.userCertificated
    ) {
      // window.appId = window.prompt('getApplicationId', '');
      obj.isIos = true;
    }
  } else if (checkAgent().isAndroid) {
    if (window.srightNativeBridge) {
      // window.appId = window.srightNativeBridge.getApplicationId();
      obj.isAndroid = true;
    }
  }
  return obj;
};
/**
 * 根据当前设备环境判断头部参数
 */
declare interface ClientType {
  [key: string]: any;
}
export const clientMap = new Map([
  ['isWechat', 1],
  ['isAndroid', 2],
  ['isIos', 3]
]);
export const clientCheckCode = () => {
  const clientObj: ClientType = checkAgent();
  const objKeys = Object.keys(clientObj);
  const h5Code = 1; // 默认h5环境
  let client = '';
  objKeys.forEach((item) => {
    if (clientObj[item]) {
      client = item;
    }
  });
  const clientCode = clientMap.get(client);
  return clientCode ? clientCode : h5Code;
};
/**
 * 时间格式化
 */
export const formatDate = {
  format(time: number, pattern = DEFAULT_PATTERN) {
    const date = new Date(time);
    function padding(s: any, len: number) {
      let newS = s;
      let newLen = len;
      newLen = len - String(newS).length;
      for (let i = 0; i < newLen; i++) {
        newS = '0' + newS;
      }
      return newS;
    }

    return pattern.replace(SIGN_REGEXP, function($0) {
      switch ($0.charAt(0)) {
        case 'Y':
          return padding(date.getFullYear(), $0.length);
        case 'M':
          return padding(date.getMonth() + 1, $0.length);
        case 'D':
          return padding(date.getDate(), $0.length);
        case 'w':
          return date.getDay() + 1;
        case 'h':
          return padding(date.getHours(), $0.length);
        case 'm':
          return padding(date.getMinutes(), $0.length);
        case 's':
          return padding(date.getSeconds(), $0.length);
      }
    });
  },
  parse(dateString: string, pattern: string) {
    const matchs1 = pattern.match(SIGN_REGEXP);
    const matchs2 = dateString.match(/(\d)+/g);
    if (matchs1 && matchs2 && matchs1.length === matchs2.length) {
      const _date = new Date(1970, 0, 1);
      for (let i = 0; i < matchs1.length; i++) {
        const _int = parseInt(matchs2[i], 10);
        const sign = matchs1[i];
        switch (sign.charAt(0)) {
          case 'y':
            _date.setFullYear(_int);
            break;
          case 'M':
            _date.setMonth(_int - 1);
            break;
          case 'd':
            _date.setDate(_int);
            break;
          case 'h':
            _date.setHours(_int);
            break;
          case 'm':
            _date.setMinutes(_int);
            break;
          case 's':
            _date.setSeconds(_int);
            break;
        }
      }
      return _date;
    }
    return null;
  }
};

/**
 * 检查客户端类型
 */
export const checkClient = () => {
  const u = navigator.userAgent;
  return {
    isAndroid: u.indexOf('Android') > -1 || u.indexOf('Adr') > -1,
    isIOS: !!u.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/)
  };
};

/**
 * 根据月份获取当月的最后一天
 * @param month
 */
export function getLastDay(month: number) {
  const date = new Date();
  const nextMonthFirstDay: any = new Date(date.getFullYear(), month, 1);
  const oneDay = 1000 * 60 * 60 * 24;
  const lastTime = new Date(nextMonthFirstDay - oneDay);
  const day = lastTime.getDate();
  return day;
}

/** 截取地址 */
export function getVideoByRate(url: string, rate = '540') {
  if (url) {
    const len = url.length;
    const videoName = url.slice(0, len - 4);
    return `${videoName}_${rate}p.mp4`;
  }
  return '';
}

/**
 * 下载文件
 * @param data
 * @param fileName
 */
export function downloadFile(data: string, fileName: string) {
  const result = new Blob([data], { type: 'application/octet-stream;charset=utf-8' }); // 接口返回的是string文件流，需要通过blob转化
  // if (window.FileReader) {
  const fileReader = new FileReader();
  fileReader.readAsDataURL(result);

  fileReader.onload = (e: any) => {
    // 文件加载
    const link = document.createElement('a');
    link.style.display = 'none';
    link.download = fileName;
    link.href = e.target.result;
    link.click();
    window.URL.revokeObjectURL(link.href);
  };
  fileReader.onerror = (error) => {
    fileReader.abort();
    // reject(error)
  };
}

export function downloadWithIframe(src: string, okCb?: () => void) {
  const id = 'iframeToDownload';
  let iframe: any = document.getElementById(id);
  if (iframe) {
    document.body.removeChild(iframe);
  }
  iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  iframe.setAttribute('id', id);
  iframe.name = 'iframe';
  document.body.appendChild(iframe);

  iframe.src = src;
  iframe.onload = () => {
    const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
    let tipText = '';
    const iframeBody = iframeDoc.body;
    if (iframeBody.innerText) {
      try {
        tipText = JSON.parse(iframeBody.innerText).message;
      } catch (c) {
        tipText = iframeDoc.innerText;
      }
      Taro.showToast({
        title: tipText || '下载文件失败',
        duration: 1000
      });
      // message.warning(tipText || '下载文件失败', 2)
      return false;
    } else if (okCb) okCb();
  };
}

/**
 * 查询地址参数
 * @param name
 */
export function getQueryString(name: string) {
  const reg = new RegExp('(^|&)' + name + '=([^&]*)(&|$)', 'i');
  const r = window.location.search.substr(1).match(reg);
  if (r !== null) return unescape(r[2]);
  return null;
}

/**
 * 获取h5定位
 * @param cb
 */
type cbParams = locationType | responseType;
type locationType = {
  latitude: number;
  longitude: number;
};
type responseType = {
  success: boolean;
  code: number;
  message?: string;
};
const response: responseType = {
  success: false,
  code: 2
};
export function getLocation(cb: (p: any) => void) {
  if (window.navigator && window.navigator.geolocation) {
    window.navigator.geolocation.getCurrentPosition(
      (p) => {
        if (p && p.coords) {
          response.success = true;
          cb(p.coords);
        } else {
          response.code = 1; //请允许系统获取您的地理位置！
          cb(response);
        }
      },
      (e) => {
        response.message = e.message;
        cb(response);
      }
    );
  } else {
    cb(response);
  }
}

/**
 *
 * 检测是否为全面屏
 * @returns
 */
export const judgeBigScreen = () => {
  // ，这里根据返回值 true 或false ,返回true的话 则为全面屏
  let result = false;
  const rate = window.screen.height / window.screen.width;
  const limit = window.screen.height === window.screen.availHeight ? 1.8 : 1.65; // 临界判断值

  if (rate > limit) {
    result = true;
  }
  return result;
};

/**
 *
 * 微信分享
 * @param title
 * @param desc
 * @param link
 * @param shareUrl
 * @param imgUrl
 * @returns
 */
export const wxShare = async ({
  title = '',
  desc = '',
  link = window.location.href,
  imgUrl = 'https://static.ergengtech.com/gengFire/home/logo_opacity.png',
  shareUrl = window.location.href
}) => {
  if (!checkAgent().isWechat) return;
  if (!window.entryUrl) {
    window.entryUrl = encodeURIComponent(link);
  }
  const res = await HomeApi.getWechatSignature({
    url: checkAgent().isAndroid ? encodeURIComponent(link) : encodeURIComponent(window.entryUrl)
  });
  if (res) {
    wx.config({
      debug: false, // 开启调试模式,调用的所有api的返回值会在客户端alert出来，若要查看传入的参数，可以在pc端打开，参数信息会通过log打出，仅在pc端时才会打印。
      appId: res.data.appId, // 必填，公众号的唯一标识
      timestamp: res.data.timestamp, // 必填，生成签名的时间戳
      nonceStr: res.data.nonceStr, // 必填，生成签名的随机串
      signature: res.data.signature, // 必填，签名
      jsApiList: ['updateAppMessageShareData', 'updateTimelineShareData'] // 必填，需要使用的JS接口列表
    });
    // 分享落地页
    wx.ready(() => {
      wx.updateAppMessageShareData({
        title, // 分享标题
        desc, // 分享描述
        link: shareUrl, // 分享链接，该链接域名或路径必须与当前页面对应的公众号JS安全域名一致
        imgUrl, // 分享图标
        success: () => {
          // 设置成功
        },
        error: () => {}
      });
      wx.updateTimelineShareData({
        title, // 分享标题
        desc, // 分享描述
        link: shareUrl, // 分享链接，该链接域名或路径必须与当前页面对应的公众号JS安全域名一致
        imgUrl, // 分享图标
        success: function() {
          // 设置成功
        }
      });
    });
    wx.error(function() {});
  }
};
export const newWxShare = async ({
  subId = '',
  title = '',
  desc = '',
  link = window.location.href,
  imgUrl = 'https://static.ergengtech.com/gengFire/home/logo_opacity.png',
  shareUrl = window.location.href
}) => {
  if (!checkAgent().isWechat) return;
  if (!window.entryUrl) {
    window.entryUrl = encodeURIComponent(link);
  }
  const res = await HomeApi.getWechatSignature({
    url: checkAgent().isAndroid ? encodeURIComponent(link) : encodeURIComponent(window.entryUrl)
  });
  if (res) {
    wx.config({
      debug: false, // 开启调试模式,调用的所有api的返回值会在客户端alert出来，若要查看传入的参数，可以在pc端打开，参数信息会通过log打出，仅在pc端时才会打印。
      appId: res.data.appId, // 必填，公众号的唯一标识
      timestamp: res.data.timestamp, // 必填，生成签名的时间戳
      nonceStr: res.data.nonceStr, // 必填，生成签名的随机串
      signature: res.data.signature, // 必填，签名
      jsApiList: ['updateAppMessageShareData', 'updateTimelineShareData'] // 必填，需要使用的JS接口列表
    });
    // 分享落地页
    wx.ready(() => {
      wx.updateAppMessageShareData({
        title, // 分享标题
        desc, // 分享描述
        link: shareUrl, // 分享链接，该链接域名或路径必须与当前页面对应的公众号JS安全域名一致
        imgUrl, // 分享图标
        success: () => {
          shootInfoApi.openShared({
            suborderId: subId
          });
          // 设置成功
        },
        error: () => {}
      });
      wx.updateTimelineShareData({
        title, // 分享标题
        desc, // 分享描述
        link: shareUrl, // 分享链接，该链接域名或路径必须与当前页面对应的公众号JS安全域名一致
        imgUrl, // 分享图标
        success: function() {
          shootInfoApi.openShared({
            suborderId: subId
          });
          // 设置成功
        }
      });
    });
    wx.error(function() {});
  }
};
/**
 * 调用app登录组件
 */
export const callAppLogin = () => {
  if (checkAppClient().isAndroid) {
    window.token = null;
    // token失效调用安卓客户端登录
    window.srightNativeBridge.requestLogin('afterLoginByH5');
    window.afterLoginByH5 = function() {
      window.location.reload();
    };
    return true;
  } else if (checkAppClient().isIos) {
    window.token = null;
    // token失效调用苹果客户端登录
    const str = window.prompt('login', '1');
    if (str) {
      window.location.reload();
    } else {
      window.webkit.messageHandlers.popPage.postMessage(0);
    }
    return true;
  }
  return false;
};

/**
 * 选择图片/视频
 * @param param0
 */
export const chooseMedia = ({ type }) => {
  return new Promise((resolve, reject) => {
    if (type === 'video') {
      Taro.chooseVideo({
        sourceType: ['album', 'camera'],
        maxDuration: 180,
        camera: 'back',
        success: function(res) {
          if (res.tempFiles[0].size > 140 * 1020 * 1020) {
            Taro.showToast({
              icon: 'none',
              title: '视频大小不能超过140M'
            });
            reject('视频大小不能超过140M');
            return;
          }
          let fileSuffix = res.tempFiles[0].type.split('/')[1];
          if (fileSuffix === 'quicktime') fileSuffix = 'mp4';
          resolve({ filePath: res.tempFilePaths[0], fileSuffix });
        },
        fail: (e) => {
          reject(e);
        }
      });
    } else if (type === 'img') {
      Taro.chooseImage({
        count: 1, // 默认9
        sizeType: ['original', 'compressed'], // 可以指定是原图还是压缩图，默认二者都有
        sourceType: ['album', 'camera'], // 可以指定来源是相册还是相机，默认二者都有，在H5浏览器端支持使用 `user` 和 `environment`分别指定为前后摄像头
        success: function(res) {
          // 返回选定照片的本地文件路径列表，tempFilePath可以作为img标签的src属性显示图片
          const fileSuffix = res.tempFiles[0].type.split('/')[1];
          resolve({ filePath: res.tempFilePaths[0], fileSuffix });
        },
        fail: (e) => {
          reject(e);
        }
      });
    }
  });
};
/**
 * 上传图片/视频
 * @param param0
 */
export const uploadMedia = ({
  url,
  filePath,
  fileName,
  header = {},
  formData = {},
  name = 'file'
}: any) => {
  return new Promise((resolve, reject) => {
    Taro.showLoading({
      title: '上传中...'
    });
    Taro.uploadFile({
      url,
      filePath,
      name,
      formData,
      fileName,
      header,
      success(res) {
        const data = JSON.parse(res.data);
        if (data.code === 200) {
          resolve(data.data?.fileUrl);
        } else {
          reject(data.message);
          Taro.showToast({
            title: `上传失败`,
            icon: 'none'
          });
        }
      },
      fail: (e) => {
        reject(e);
      }
    });
  });
};
//文件上传
export const uploadFile = ({
  url,
  filePath,
  fileName,
  header = {},
  formData = {},
  name = 'file'
}: any) => {
  return new Promise((resolve, reject) => {
    Taro.uploadFile({
      url,
      filePath,
      name,
      formData,
      fileName,
      header,
      success(res) {
        const data = JSON.parse(res.data);
        if (data.code === 200) {
          resolve(data.data?.fileUrl);
        } else {
          reject(data.message);
          Taro.showToast({
            title: `上传失败`,
            icon: 'none'
          });
        }
      },
      fail: (e) => {
        reject(e);
      }
    });
  });
};
/**
 * 呼叫客服电话
 */
export const callSerVicePhone = () => {
  const app: any = Current.app;
  Taro.makePhoneCall({
    phoneNumber: app.globalData.servicePhone
  });
};

export const getH5Location = () => {
  return new Promise((resolve, reject) => {
    if (window.navigator && window.navigator.geolocation) {
      window.navigator.geolocation.getCurrentPosition(
        (p) => {
          if (p && p.coords) {
            resolve(p.coords);
          } else {
            reject('请允许系统获取您的地理位置！'); //请允许系统获取您的地理位置！
          }
        },
        (e) => {
          reject(e.message);
        }
      );
    } else {
      reject('获取定位失败！');
    }
  });
};

export const getCityCode = () => {
  return new Promise(async (resolve, reject) => {
    getH5Location()
      .then(async (coords: any) => {
        if (coords.latitude && coords.longitude) {
          const locObj = {
            lat: coords.latitude,
            lng: coords.longitude
          };
          const res = await HomeApi.getUserLocation(locObj);
          if (res && res.code === 200) {
            // r(res.data)
            if (res.data) {
              setStorage('h5-cityCode', res.data?.cityCode);
            }
            resolve(res.data);
          } else {
            reject('获取cityCode失败！');
          }
        } else {
          reject('获取定位失败！');
        }
      })
      .catch((e) => {
        reject(e);
      });
  });
};

export const getFormattedCaseData = (data: any) => {
  const prevData = [...data];
  const finalData: any = [];
  prevData.reduce((prev: any, item: any, index: number) => {
    if (prev.length === 2) {
      prev = [item];
    } else {
      prev.push(item);
    }
    if (prev.length === 2) {
      finalData.push(prev);
    }
    if (prev.length === 1 && index === data.length - 1) {
      finalData.push(prev);
    }
    return prev;
  }, []);
  return finalData;
};
export const timeFormat2Day = (date: Date) => {
  const Y = date.getFullYear();
  const M = date.getMonth() + 1;
  const D = date.getDate();
  return Y + '-' + (M < 10 ? '0' + M : M) + '-' + (D < 10 ? '0' + D : D);
};

// 获取系统版本
export const getOsVersion = () => {
  const u = navigator.userAgent;
  let version = '';
  if (u.indexOf('Mac OS X') > -1) {
    // ios
    const regStr_saf = /OS [\d._]*/gi;
    const verinfo = u.match(regStr_saf);
    version = 'IOS' + (verinfo + '').replace(/[^0-9|_.]/gi, '').replace(/_/gi, '.');
  } else if (u.indexOf('Android') > -1 || u.indexOf('Linux') > -1) {
    // android
    version =
      'Android' +
      u.substr(
        u.indexOf('Android') + 8,
        u.indexOf(';', u.indexOf('Android')) - u.indexOf('Android') - 8
      );
  } else if (u.indexOf('BB10') > -1) {
    // 黑莓bb10系统
    version =
      'blackberry' +
      u.substr(u.indexOf('BB10') + 5, u.indexOf(';', u.indexOf('BB10')) - u.indexOf('BB10') - 5);
  } else if (u.indexOf('IEMobile') > -1) {
    // windows phone
    version =
      'winphone' +
      u.substr(
        u.indexOf('IEMobile') + 9,
        u.indexOf(';', u.indexOf('IEMobile')) - u.indexOf('IEMobile') - 9
      );
  } else {
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.indexOf('windows nt 5.0') > -1) {
      version = 'Windows 2000';
    } else if (
      userAgent.indexOf('windows nt 5.1') > -1 ||
      userAgent.indexOf('windows nt 5.2') > -1
    ) {
      version = 'Windows XP';
    } else if (userAgent.indexOf('windows nt 6.0') > -1) {
      version = 'Windows Vista';
    } else if (userAgent.indexOf('windows nt 6.1') > -1 || userAgent.indexOf('windows 7') > -1) {
      version = 'Windows 7';
    } else if (userAgent.indexOf('windows nt 6.2') > -1 || userAgent.indexOf('windows 8') > -1) {
      version = 'Windows 8';
    } else if (userAgent.indexOf('windows nt 6.3') > -1) {
      version = 'Windows 8.1';
    } else if (
      userAgent.indexOf('windows nt 6.2') > -1 ||
      userAgent.indexOf('windows nt 10.0') > -1
    ) {
      version = 'Windows 10';
    } else {
      version = 'Unknown';
    }
  }
  return version;
};

export const detectDeviceModel = () => {
  const md = new MobileDetect(navigator.userAgent);
  let os = md.os(); //获取系统
  const deviceType = navigator.userAgent;
  let model = '';

  const contains = (sss: string[], needle: string) => {
    for (const i in sss) {
      if (sss[i].indexOf(needle) > 0) return i;
    }
    return -1;
  };

  if (os == 'iOS') {
    //ios系统的处理
    os = md.os() + md.version('iPhone');
    model = md.mobile();
  } else if (os == 'AndroidOS') {
    //Android系统的处理
    os = md.os() + md.version('Android');
    const sss = deviceType.split(';');
    const i: any = contains(sss, 'Build/');
    if (i > -1) {
      model = sss[i].substring(0, sss[i].indexOf('Build/'));
    }
  }
  return model;
};
